import React, { useState } from "react";
import { useActor } from "@xstate/react";
import TextInput, { isValidTextInput } from "../TextInput";
import { Button } from "../SignUpButton";
import { Loader } from "@pushpress/shared-components";
import { useLoginWithCodeQuery } from "../../../../apis/loginApi.hooks";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useSignUpMachine } from "../../context/SignUpMachineContext";
import { TextInputType } from "../../constants/signup.types";

const AccountConfirmationStep: React.FC = () => {
  const machine = useSignUpMachine();
  const [state, send] = useActor(machine);
  const { formData, clientUuid } = state.context;

  const [isLoading, setIsLoading] = useState(false);
  const loginWithCode = useLoginWithCodeQuery();

  const codeValue = formData["code"];
  const emailValue = formData["email"];

  const field = {
    id: "code",
    label: "Confirmation Code",
    inputType: "text",
    required: true,
  };

  const handleChange = (value: any) => {
    send({
      type: "UPDATE_FORM",
      data: { [field.id]: value },
    });
  };

  const handleSubmit = async () => {
    if (!clientUuid || !emailValue || !codeValue) {
      toast.error("Missing information needed to confirm code.");
      return;
    }
    setIsLoading(true);

    try {
      const loginResult = await loginWithCode.mutateAsync({
        clientUuid,
        email: emailValue,
        code: codeValue,
      });

      if (loginResult) {
        const expires = new Date();
        expires.setDate(expires.getDate() + 60);
        const domainBase = process.env.BASE_URL || "pushpress.com";
        Cookies.set("ACCESS_TOKEN", loginResult.accessToken, {
          domain: `.${domainBase}`,
          expires,
        });
        Cookies.set("REFRESH_TOKEN", loginResult.refreshToken, {
          domain: `.${domainBase}`,
          expires,
        });
        Cookies.set("USER_PROFILE_ID", loginResult.profileUuid, {
          domain: `.${domainBase}`,
          expires,
        });
        send({ type: "NEXT" });
      } else {
        toast.error("Invalid code. Please try again.");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Login with code failed:", error);
      toast.error("Invalid code or server error. Please try again.");
      setIsLoading(false);
    }
  };

  const canProceed = isValidTextInput(
    codeValue,
    field.inputType as TextInputType,
  );

  return (
    <>
      <div className="tw-flex tw-flex-col tw-items-center tw-space-y-1 tw-text-center">
        <h2 className="tw-text-xl">Verify Your Email</h2>
        <p className="tw-text-gray-400 tw-text-base">
          We&apos;ve sent a code to your email
        </p>
      </div>
      <div className="tw-space-y-4">
        <TextInput
          key={field.id}
          id={field.id}
          value={codeValue}
          onChange={handleChange}
          inputType={field.inputType as TextInputType}
          label={field.label}
          required={field.required}
          disabled={isLoading}
        />
      </div>
      <div className="tw-flex tw-justify-between tw-items-center">
        <Button
          onClick={handleSubmit}
          disabled={!canProceed || isLoading}
          size="fat"
          className="tw-uppercase tw-tracking-widest tw-w-full tw-flex tw-py-4 tw-items-center tw-gap-2 tw-text-white"
        >
          {isLoading ? <Loader color="white" /> : <span>Confirm</span>}
        </Button>
      </div>
    </>
  );
};

export default AccountConfirmationStep;
